import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  PaginatedElasticSearchBusinessList,
  PaginatedElasticSearchPublicList,
} from '@offconon/core-api';

import * as SearchBoxActions from './elastic-search-box.actions';
import { ElasticSearchboxEntity, EntityType, SearchData } from './elastic-search-box.models';

export const ELASTICSEARCHBOX_FEATURE_KEY = 'elastic-search-box';

export interface ElasticSearchBoxState extends EntityState<ElasticSearchboxEntity> {
  pagination: { page: number; page_size: number };
  searchJson: SearchData;
  searchResult: PaginatedElasticSearchPublicList;
  searchBusinessResult: PaginatedElasticSearchBusinessList;
  loading: boolean;
}

export interface ElasticSearchBoxPartialState {
  readonly [ELASTICSEARCHBOX_FEATURE_KEY]: ElasticSearchBoxState;
}

export function extractSearchDataFromJson(json: any): SearchData {
  const query = json.translations || '';

  const entity_type_list: EntityType[] = Object.entries(filtersHelper)
    .filter(([key]) => json[key] === true)
    .map(([, value]) => value);

  return { query, entity_type_list };
}

export const elasticSearchBoxAdapter: EntityAdapter<ElasticSearchboxEntity> =
  createEntityAdapter<ElasticSearchboxEntity>();

export const filtersHelper = {
  is_ads: 'ad',
  is_apps: 'app',
  is_businesses: 'business',
  is_catalogs: 'catalog',
  is_members: 'user',
} as const;

export const initialElasticSearchBoxState: ElasticSearchBoxState =
  elasticSearchBoxAdapter.getInitialState({
    pagination: { page: 1, page_size: 10 },
    selectedFilters: [],
    searchJson: { query: '', entity_type_list: [] },
    searchResult: {},
    searchBusinessResult: {},
    loading: false,
  });

const reducer = createReducer(
  initialElasticSearchBoxState,
  on(SearchBoxActions.initElasticSearchBox, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(SearchBoxActions.setSearchParams, (state, { searchJson }) => {
    return {
      ...state,
      searchJson: extractSearchDataFromJson(searchJson),
    };
  }),
  on(SearchBoxActions.getSearchResults, (state, {}) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(SearchBoxActions.getSearchResultsSuccess, (state, { searchResults }) => {
    return {
      ...state,
      searchResult: searchResults,
      loading: false,
    };
  }),
  on(SearchBoxActions.getPaginatedSearchResultsSuccess, (state, { searchResults }) => {
    return {
      ...state,
      searchResult: {
        ...searchResults,
        current_page_number: searchResults.current_page_number,
        results: [...(state.searchResult?.results ?? []), ...(searchResults.results ?? [])],
      },
      loading: false,
    };
  }),
  on(SearchBoxActions.getSearchResultsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(SearchBoxActions.getBusinessSearchResultsSuccess, (state, { searchBusinessResults }) => {
    return {
      ...state,
      searchBusinessResult: searchBusinessResults,
      loading: false,
    };
  }),
  on(
    SearchBoxActions.getPaginatedBusinessSearchResultsSuccess,
    (state, { searchBusinessResults }) => {
      return {
        ...state,
        searchBusinessResult: {
          ...searchBusinessResults,
          current_page_number: searchBusinessResults.current_page_number,
          results: [
            ...(state.searchResult?.results ?? []),
            ...(searchBusinessResults.results ?? []),
          ],
        },
        loading: false,
      };
    },
  ),
);
export function elasticSearchBoxReducer(
  state: ElasticSearchBoxState | undefined,
  action: Action,
): ElasticSearchBoxState {
  return reducer(state, action);
}

function removeEmptyStrings(obj: { [x: string]: any }) {
  for (const key in obj) {
    if (typeof obj[key] === 'string' && obj[key].trim() === '') {
      delete obj[key];
    }
  }
  return obj;
}
